import {
    GetSustainabilityTotalEmissionsRequestParams,
    GetSustainabilityTotalEmissionsResponseBody,
    GetSustainabilityGHGEmissionsRequestParams,
    GetSustainabilityGHGEmissionsResponseBody,
} from '@/hooks'
import useAuthStore from '../../../stores/useAuthStore/useAuthStore'
import { useAxiosInstance } from '../useAxiosInstance'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

export const useSustainabilityApi = () => {
    const accessToken = useAuthStore((state) => state.accessToken)
    const axiosInstance = useAxiosInstance(accessToken)

    const customerId =
        useAuthStore((state) => state.customer?.customerId.toString()) ?? ''

    const useTotalEmissionsQuery = (
        params: Partial<GetSustainabilityTotalEmissionsRequestParams>,
        options: useTotalEmissionsQueryOpts = { enabled: true }
    ) => {
        const sanitizedParams = useMemo(() => {
            const cleanParams = { ...params }
            if (
                (cleanParams.type === 'emissionTypes' ||
                    cleanParams.type === 'commodities') &&
                cleanParams.scope === 'scope1'
            ) {
                delete cleanParams.scope2type
            }
            return cleanParams
        }, [params])

        const query = useQuery({
            queryKey: ['total-emissions', customerId, params, accessToken],
            queryFn: async () => {
                try {
                    const response =
                        await axiosInstance.get<GetSustainabilityTotalEmissionsResponseBody>(
                            `/api/v1/customers/${customerId}/reports/sustainability/total-emissions`,
                            { params: sanitizedParams }
                        )
                    options.onSuccess?.(response.data)
                    return response.data
                } catch (error) {
                    options.onError?.()
                    throw error
                }
            },
            enabled:
                options.enabled &&
                !!accessToken &&
                !!customerId &&
                !!params['nodeIds[]'] &&
                !!params['commodityTypeIds[]'],

            placeholderData: (prev) => prev,
        })

        const downloadXlsx = useCallback(async () => {
            const response = await axiosInstance.get<Blob>(
                `/api/v1/customers/${customerId}/reports/sustainability/total-emissions`,
                {
                    params: sanitizedParams,
                    headers: {
                        Accept: 'application/vnd.ms-excel',
                    },
                    responseType: 'blob',
                }
            )
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            const filename =
                sanitizedParams.type === 'electricitySources'
                    ? 'electricity-mix-usage.xlsx'
                    : 'total-emissions.xlsx'
            link.setAttribute('download', filename)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        }, [sanitizedParams])

        return { ...query, downloadXlsx }
    }

    const useGHGEmissionsQuery = (
        params: Partial<GetSustainabilityGHGEmissionsRequestParams>,
        options: useGHGEmissionsQueryOpts = { enabled: true }
    ) => {
        const { includeTotals, ...restParams } = params

        const sanitizedParams = useMemo(() => {
            const cleanParams = { ...restParams }
            if (cleanParams.scope === 'scope1') {
                delete cleanParams.scope2type
            }
            return cleanParams
        }, [restParams])

        const query = useQuery({
            queryKey: [
                'ghg-emissions',
                customerId,
                sanitizedParams,
                accessToken,
            ],
            queryFn: async () => {
                try {
                    const response =
                        await axiosInstance.get<GetSustainabilityGHGEmissionsResponseBody>(
                            `/api/v1/customers/${customerId}/reports/sustainability/ghg-emissions`,
                            { params: sanitizedParams }
                        )
                    options.onSuccess?.(response.data)
                    return response.data
                } catch (error) {
                    options.onError?.()
                    throw error
                }
            },
            enabled:
                options.enabled &&
                !!accessToken &&
                !!customerId &&
                !!params['nodeIds[]'],
            placeholderData: (prev) => prev,
        })

        const downloadXlsx = useCallback(async () => {
            const response = await axiosInstance.get<Blob>(
                `/api/v1/customers/${customerId}/reports/sustainability/ghg-emissions`,
                {
                    params: {
                        ...sanitizedParams,
                        includeTotals,
                    },
                    headers: {
                        Accept: 'application/vnd.ms-excel',
                    },
                    responseType: 'blob',
                }
            )
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'ghg-emissions.xlsx')
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        }, [includeTotals, sanitizedParams])

        return { ...query, downloadXlsx }
    }

    return {
        useTotalEmissionsQuery,
        useGHGEmissionsQuery,
    }
}

type useTotalEmissionsQueryOpts = {
    enabled: boolean
    onSuccess?: (data: GetSustainabilityTotalEmissionsResponseBody) => void
    onError?: () => void
}

type useGHGEmissionsQueryOpts = {
    enabled: boolean
    onSuccess?: (data: GetSustainabilityGHGEmissionsResponseBody) => void
    onError?: () => void
}
